import { Subscription } from "rxjs";
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { BubbleConferenceServiceEvents, BubbleConferenceServiceRB } from "./bubbleConference.service";
import { WebConferenceSession } from "rainbow-web-sdk/models/webConferenceSession.model";
import { BubbleConferenceParticipant } from "rainbow-web-sdk/models/webConferenceParticipant.model";
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';

export class WebConferenceHelper {

    private subscription: Subscription;
    private eventService: EventService = EventService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();

    private configuration: any = {
        dynamicFeed: true,
        limitVideoSubscriptions: 0,
        disableAutoSubscriptions: false
    };

    constructor(private webConferenceService: BubbleConferenceServiceRB) { }

    public start() {
        this.attachHandlers();
    }

    public stop() {
        if (this.subscription) { this.subscription.unsubscribe(); this.subscription = null; }
    }

    public setConfiguration(disableDynamicFeed: boolean = false, disableAutoSubscriptions: boolean = false, limitVideoSubscriptions: number = 0) {
        this.logger.info(`[webConferenceHelper] setConfiguration new configuration disableDynamicFeed -- ${disableDynamicFeed}, disableAutoSubscriptions -- ${disableAutoSubscriptions}`);
        this.configuration.dynamicFeed = !disableDynamicFeed;
        this.configuration.limitVideoSubscriptions = limitVideoSubscriptions;
        this.configuration.disableAutoSubscriptions = disableAutoSubscriptions;
    }

    public async updateSubstreamLevelForDynamicFeedBasedOnVideoSize(conferenceId: string, width: any): Promise<void> {
        try {
            const webConferenceSession = this.webConferenceService.getWebConferenceSessionById(conferenceId);
            if (!webConferenceSession || !webConferenceSession?.dynamicFeedSession || !width) {
                throw new Error("[WebConferenceHelper] updateSubstreamLevelForDynamicFeedBasedOnVideoSize -- missing parameters");
            }
            let subStreamLevel = 1;
            if (width >= 854) { subStreamLevel = 2; }
            if (width <= 320) { subStreamLevel = 0; }

            //check if this is dynamic feed

            if (!webConferenceSession.dynamicFeedSession.subStreamLevel) webConferenceSession.dynamicFeedSession.subStreamLevel = 0;

            //update only if there's no quality limitation on the substream level
            if (subStreamLevel !== webConferenceSession.dynamicFeedSession.subStreamLevel) {
                webConferenceSession.dynamicFeedSession.subStreamLevel = subStreamLevel;
                this.logger.info(`[webConferenceHelper] updateSubstreamLevelForDynamicFeedBasedOnVideoSize -- ${conferenceId} -- ${subStreamLevel}`);
                const params = { option: "update", subStreamLevel: subStreamLevel };
                const body = JSON.stringify(params);
                const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/users/${webConferenceSession.localParticipant.id}`;
                const headers = this.authService.getPostHeader();
                const response = await fetch(url, { method: 'PUT', headers, body });
                if (!response.ok) throw response;
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateSubstreamLevelForDynamicFeedBasedOnVideoSize');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async updateSubstreamLevelForParticipantBasedOnVideoSize(conferenceId: string, publisher: BubbleConferenceParticipant, width: any): Promise<void> {
        try {
            const webConferenceSession = this.webConferenceService.getWebConferenceSessionById(conferenceId);
            if (!webConferenceSession || !publisher || !width) {
                throw new Error("[WebConferenceHelper] updateSubstreamLevelForParticipantBasedOnVideoSize -- missing parameters");
            }
            let subStreamLevel = 1;
            if (width >= 854) { subStreamLevel = 2; }
            if (width <= 320) { subStreamLevel = 0; }

            //check if this is dynamic feed

            //save the new video element size substream level
            publisher.videoElementSizeSubstreamLevel = subStreamLevel;

            //update only if there's no quality limitation on the substream level
            if (subStreamLevel !== publisher.subStreamLevel && subStreamLevel <= publisher.quailityLimitationSubstreamLevel) {
                publisher.subStreamLevel = subStreamLevel;
                this.logger.info(`[webConferenceHelper] updateSubstreamLevelForParticipantBasedOnVideoSize -- ${conferenceId} -- ${publisher.id} -- ${subStreamLevel}`);
                const params = { option: "update", subStreamLevel: subStreamLevel, publisherId: publisher.id };
                const body = JSON.stringify(params);
                const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/users/${webConferenceSession.localParticipant.id}`;
                const headers = this.authService.getPostHeader();
                const response = await fetch(url, { method: 'PUT', headers, body });
                if (!response.ok) throw response;
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateSubstreamLevelForParticipantBasedOnVideoSize');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    /*
        // ===> If we detect less than 10 FPS for video, we downgrade the quality of the video
        Layers combinaison that we will use:
        Spatial  (bitrate)    Temporal (framerate)
        2 (high).              1 (high)
        1 (medium)             1(high)
        1 (medium).            0 (low)
        0 (low)                1(high)
        0(low)                 0 (low)
    */
    public async updateParticipantVideoOnQualityChange(webConferenceSession: WebConferenceSession, publisher: BubbleConferenceParticipant, decreaseQuality: boolean, session: any): Promise<void> {
        try {
            if (!webConferenceSession || !publisher) {
                throw new Error("[WebConferenceHelper] updateParticipantVideoOnQualityChange -- missing parameters");
            }

            let newSubStreamLevel = publisher.subStreamLevel;
            // const newTemporalLevel = publisher.temporalLevel;

            if (decreaseQuality && publisher.subStreamLevel > 0) {
                newSubStreamLevel = publisher.subStreamLevel - 1;
            }
            else if (!decreaseQuality && publisher.subStreamLevel < 2) {
                newSubStreamLevel = publisher.subStreamLevel + 1;
            }

            //go one level down on the quality
            // if (decreaseQuality) {
            //     if (publisher.subStreamLevel === 2 || (publisher.subStreamLevel === 1 && publisher.temporalLevel === 0)) {
            //         newSubStreamLevel = publisher.subStreamLevel - 1;
            //         newTemporalLevel = 1;
            //     }
            //     else if (publisher.subStreamLevel === 0 || (publisher.subStreamLevel === 1 && publisher.temporalLevel === 1)) {
            //         newTemporalLevel = 0;
            //     }
            // }
            // else {
            //     //go back up slowly
            //     //for the coming back on quality, we'll play on the temporal level, the other part will be managed by the UI
            //     if (publisher.subStreamLevel === 0 && publisher.temporalLevel === 0) {
            //         newTemporalLevel = 1;
            //     }
            //     else if (publisher.subStreamLevel === 0 && publisher.temporalLevel === 1) {
            //         newSubStreamLevel = 1;
            //     }
            //     else if (publisher.subStreamLevel === 1 && publisher.temporalLevel === 0) {
            //         //remove quality limitation
            //         newTemporalLevel = 1;
            //     }
            //     else {
            //         //remove quality limitation
            //         newSubStreamLevel = 2;
            //     }
            // }

            //save the new limitation substream level
            publisher.quailityLimitationSubstreamLevel = newSubStreamLevel;

            // const shouldUpdate = ((newSubStreamLevel !== publisher.subStreamLevel && newSubStreamLevel <= publisher.videoElementSizeSubstreamLevel) || newTemporalLevel !== publisher.temporalLevel);
            const shouldUpdate = (newSubStreamLevel !== publisher.subStreamLevel && newSubStreamLevel <= publisher.videoElementSizeSubstreamLevel);

            if (shouldUpdate) {
                this.logger.info(`[webConferenceHelper] updateParticipantVideoOnQualityChange -- ${webConferenceSession.id} -- ${publisher.id} -- substream: ${newSubStreamLevel}`);
                const params = { option: "update", subStreamLevel: newSubStreamLevel, publisherId: publisher.id, temporalLevel: 2 };
                const body = JSON.stringify(params);
                const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${webConferenceSession.id}/users/${webConferenceSession.localParticipant.id}`;
                const headers = this.authService.getPostHeader();
                const response = await fetch(url, { method: 'PUT', headers, body });
                if (!response.ok) throw response;
                publisher.subStreamLevel = newSubStreamLevel;
                // publisher.temporalLevel = newTemporalLevel;
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateParticipantVideoOnQualityChange');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    private attachHandlers() {
        this.subscription = this.webConferenceService.eventSubscribe((event: RBEvent) => {
            try {
                if (event.name === BubbleConferenceServiceEvents.ON_WEBCONFERENCE_LOCKSTATE_UPDATE) {
                    this.eventService.publish("ON_CONFERENCE_LOCKED_EVENT", event.data.isLock, event.data.lockedBy);
                }
                else if (event.name === BubbleConferenceServiceEvents.ON_WEBCONFERENCE_PUBLISHER_ADD) {
                    this.subscribeToNewPublishers(event.data.roomDbId);
                }
                else if (event.name === BubbleConferenceServiceEvents.ON_WEBCONFERENCE_SERVICE_UPDATE) {
                    this.subscribeToNewServices(event.data.roomDbId);
                }
            }
            catch (_err) {
                //do nothing
            }
        });
    }

    private subscribeToNewPublishers(confId: string) {
        const conferenceSession: WebConferenceSession = this.webConferenceService.getWebConferenceSessionById(confId);
        const subscriptionWaitingQueue = [];
        if (conferenceSession && conferenceSession.participants.length) {
            if ((this.configuration.dynamicFeed && !conferenceSession.disableAutoSubscriptions) && !conferenceSession.dynamicFeedSession && !conferenceSession.dynamicFeedSubscriptionInProgress) {
                this.webConferenceService.subscribeToDynamicFeed(conferenceSession);
            }

            if (this.configuration.disableAutoSubscriptions || conferenceSession.disableAutoSubscriptions) return;

            conferenceSession.participants.forEach((participant: BubbleConferenceParticipant) => {
                if (participant.isVideoAvailable && !participant.subscriptionInProgress && !participant.videoSession) {
                    subscriptionWaitingQueue.push(participant);
                }
            });
        }

        if (subscriptionWaitingQueue.length) {
            this.subscribeToPublishersByParts(conferenceSession, subscriptionWaitingQueue);
        }
    }

    private async subscribeToPublishersByParts(conferenceSession: WebConferenceSession, waitingList: any[], size: number = 12) {
        const subscriptionPromises = [];
        for (let i = 0; i < size; i++) {
            if (waitingList[0]) {
                const participant = waitingList.shift();
                if (participant.isVideoAvailable && !participant.subscriptionInProgress && !participant.videoSession) {
                    subscriptionPromises.push(this.webConferenceService.subscribeToParticipantVideo(conferenceSession, participant));
                }
            }
        }

        if (subscriptionPromises.length) {
            await Promise.all(subscriptionPromises);
            if (waitingList.length) {
                this.subscribeToPublishersByParts(conferenceSession, waitingList);
            }
        }
    }

    private subscribeToNewServices(confId: string) {
        const conferenceSession: WebConferenceSession = this.webConferenceService.getWebConferenceSessionById(confId);
        if (conferenceSession && conferenceSession.services.length && !conferenceSession.subscribedToGridService) {
            //find the grid service
            conferenceSession.services.forEach((service) => {
                if (service.name === "video-compositor") {
                    this.webConferenceService.subscribeToService(conferenceSession, service.id);
                }
            });
        }
    }
}
